import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { trackReserveClick } from "@/lib/analytics";
import { CheckCircle2, XCircle, Loader2, Mail, ArrowRight } from "lucide-react";
import { useI18n } from "@/i18n";

export default function CarteCadeauSucces() {
  const { t, lang, localePath, alternateUrls } = useI18n();
  useSEO({
    title: t("giftcardSuccess.success"),
    description: lang === "en" ? "Confirmation of your L'Entracte gift card purchase." : "Confirmation de votre achat de carte cadeau L'Entracte.",
    lang,
    alternates: alternateUrls(),
  });
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({
    status: "loading",
    payment_status: null,
    gift_card_code: null,
    email_sent: false,
    beneficiary_name: null,
  });
  const pollRef = useRef({ attempts: 0, maxAttempts: 8 });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: "missing_session" });
      return;
    }
    let cancelled = false;

    const poll = async () => {
      pollRef.current.attempts += 1;
      try {
        const r = await api.get(`/giftcards/status/${sessionId}`);
        if (cancelled) return;
        const data = r.data || {};
        if (data.payment_status === "paid") {
          setState({
            status: "success",
            payment_status: "paid",
            gift_card_code: data.gift_card_code,
            email_sent: !!data.email_sent,
            beneficiary_name: data.beneficiary_name,
          });
          return;
        }
        if (data.status === "expired" || data.status === "canceled") {
          setState({ status: "error", payment_status: data.status });
          return;
        }
        if (pollRef.current.attempts >= pollRef.current.maxAttempts) {
          setState({ status: "timeout", payment_status: data.payment_status });
          return;
        }
        setTimeout(poll, 2500);
      } catch (e) {
        if (cancelled) return;
        if (pollRef.current.attempts >= pollRef.current.maxAttempts) {
          setState({ status: "error", payment_status: "fetch_error" });
        } else {
          setTimeout(poll, 2500);
        }
      }
    };
    poll();

    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="giftcard-success-page" className="px-6 md:px-12 lg:px-16">
      <section className="py-24 md:py-32 max-w-2xl mx-auto text-center">
        {state.status === "loading" && (
          <div data-testid="giftcard-loading">
            <Loader2 size={48} className="text-[#B8956A] mx-auto animate-spin" />
            <p className="overline mt-8">{t("giftcardSuccess.checking")}</p>
            <h1 className="font-display text-4xl mt-4">{t("giftcardSuccess.checkingTitle")}</h1>
            <p className="text-[#697585] mt-4">{t("giftcardSuccess.checkingSub")}</p>
          </div>
        )}

        {state.status === "success" && (
          <div data-testid="giftcard-success">
            <CheckCircle2 size={56} className="text-[#B8956A] mx-auto" />
            <p className="overline mt-8">{t("giftcardSuccess.success")}</p>
            <h1 className="font-display text-5xl md:text-6xl mt-4 leading-[0.95]">
              {t("giftcardSuccess.thanks")}<br />
              <span className="italic text-[#B8956A]">{t("giftcardSuccess.ready")}</span>
            </h1>
            <p className="text-[#697585] mt-6 text-lg">
              {state.beneficiary_name
                ? <>{t("giftcardSuccess.forBenef")} <strong className="text-[#203240]">{state.beneficiary_name}</strong> {t("giftcardSuccess.hasBeen")}</>
                : t("giftcardSuccess.generic")}
            </p>

            <div className="mt-10 border border-[#B8956A]/40 bg-[#FFFFFF] p-8">
              <p className="overline">{t("giftcardSuccess.yourCode")}</p>
              <p data-testid="giftcard-code" className="font-mono text-2xl md:text-3xl tracking-[0.3em] text-[#203240] mt-4 select-all">
                {state.gift_card_code}
              </p>
              <p className="text-xs text-[#697585] mt-3">{t("giftcardSuccess.keepCode")}</p>
            </div>

            <div className="mt-8 inline-flex items-center gap-3 text-sm text-[#697585]">
              <Mail size={16} className="text-[#B8956A]" />
              {state.email_sent ? t("giftcardSuccess.emailSent") : t("giftcardSuccess.emailComing")}
            </div>

            <div className="mt-12 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to={localePath("/")} className="btn-ghost justify-center">{t("giftcardSuccess.backHome")}</Link>
              <Link to={localePath("/reservation")} data-testid="giftcard-success-reserve" onClick={() => trackReserveClick("giftcard_success")} className="btn-primary justify-center">
                {t("giftcardSuccess.reserveTable")} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        )}

        {state.status === "timeout" && (
          <div>
            <Loader2 size={48} className="text-[#697585] mx-auto" />
            <p className="overline mt-8">{t("giftcardSuccess.processing")}</p>
            <h1 className="font-display text-4xl mt-4">{t("giftcardSuccess.stillFew")}</h1>
            <p className="text-[#697585] mt-4">{t("giftcardSuccess.stillBody")}</p>
            <Link to={localePath("/")} className="btn-ghost mt-8 justify-center inline-flex">{t("giftcardSuccess.backHome")}</Link>
          </div>
        )}

        {state.status === "error" && (
          <div data-testid="giftcard-error-state">
            <XCircle size={48} className="text-red-500 mx-auto" />
            <p className="overline mt-8 text-red-500">{t("giftcardSuccess.errorTitle")}</p>
            <h1 className="font-display text-4xl mt-4">{t("giftcardSuccess.errorH1")}</h1>
            <p className="text-[#697585] mt-4">{t("giftcardSuccess.errorBody")}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to={localePath("/carte-cadeau")} className="btn-primary justify-center">{t("giftcardSuccess.retry")}</Link>
              <Link to={localePath("/contact")} className="btn-ghost justify-center">{t("giftcardSuccess.contactUs")}</Link>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
